import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, PanelHeader } from "@/components/Common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardCheck, ListChecks, AlertTriangle } from "lucide-react";

const STATUS_CLS = {
  Pending: "border-amber-300 bg-amber-50 text-amber-800",
  "Future Integration": "border-indigo-300 bg-indigo-50 text-indigo-800",
};
const Blocks = ({ on }) => (
  <span className={on ? "font-semibold text-rose-600" : "text-slate-400"}>{on ? "Yes" : "No"}</span>
);

export default function AssumptionsRegister() {
  const [data, setData] = useState(null);
  useEffect(() => { api.assumptions().then(setData).catch(() => {}); }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading assumptions & dependencies…</div>;

  return (
    <div>
      <PageHeader testid="assumptions-header" eyebrow="Pilot Validation · Admin"
        title="Assumptions & Dependencies"
        subtitle="Outstanding domain inputs are surfaced here so unknowns never quietly become software assumptions. RequiSure invents no payer rule, code, or integration that has not been confirmed." />

      <div className="mb-5 flex items-start gap-2.5 rounded-md border-l-4 border-l-amber-500 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        Every item below is a real dependency. Where authoritative information is outstanding, RequiSure shows the correct pending / unavailable / unable-to-evaluate state rather than fabricating an answer.
      </div>

      <Panel className="mb-6" data-testid="assumptions-register">
        <PanelHeader label="Dependency Register" icon={ClipboardCheck}
          right={<span className="text-[11px] text-slate-400">{data.register.length} items</span>} />
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead><TableHead>Owner</TableHead><TableHead>Status</TableHead>
                <TableHead>Blocks v0.2</TableHead><TableHead>Blocks shadow</TableHead>
                <TableHead>Blocks production</TableHead><TableHead>Evidence required to close</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.register.map((r, i) => (
                <TableRow key={i} data-testid={`assumption-${i}`}>
                  <TableCell className="text-xs font-medium text-slate-800">{r.item}</TableCell>
                  <TableCell className="text-xs text-slate-600">{r.owner}</TableCell>
                  <TableCell>
                    <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_CLS[r.status] || "border-slate-300 bg-slate-100 text-slate-600"}`}>{r.status}</span>
                  </TableCell>
                  <TableCell className="text-xs"><Blocks on={r.blocks_v02} /></TableCell>
                  <TableCell className="text-xs"><Blocks on={r.blocks_shadow} /></TableCell>
                  <TableCell className="text-xs"><Blocks on={r.blocks_production} /></TableCell>
                  <TableCell className="text-[11px] text-slate-500">{r.evidence_required}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Panel>

      <Panel data-testid="pilot-site-criteria">
        <PanelHeader label="Pilot-Site Selection Criteria" icon={ListChecks} />
        <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.pilot_site_criteria.map((c, i) => (
            <div key={i} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-xs" data-testid={`site-criterion-${i}`}>
              <span className="text-slate-700">{c.criterion}</span>
              <span className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">{c.status}</span>
            </div>
          ))}
        </div>
        <p className="px-4 pb-3 text-[11px] text-slate-400">The first pilot should test RequiSure — not five other transformation projects at once. Prefer a site already using electronic ordering with a known EMR and a willing clinical champion.</p>
      </Panel>
    </div>
  );
}
